import { Trans, useTranslation } from "react-i18next";

import { ALL_DATABASES, bulkItemTitleMap } from "../PrivilegesTable/constant";
import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_IGNORE_ERROR,
  ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { PermissionError, PrivilegesState } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";
import { MissingPermissionError } from "./MissingPermissionError";

type Props = {
  error: PermissionError;
  resource: string;
};

export const MissingDatabasePermission = (props: Props) => {
  const { t } = useTranslation();
  const { error, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();

  const { errorSource } = error;
  const { name, actions } = errorSource;

  const addRelatedPermissions = () => {
    const { resource, action, object } = error;
    const resourceGroups = privilegesState[resource as keyof PrivilegesState];
    const availableGroupByObjects = resourceGroups.findIndex(group => {
      return group.objects.length <= 1 && group.objects[0] === object;
    });
    const availableGroupByActions = resourceGroups.findIndex(group => {
      return group.toAssign.length === 1 && group.toAssign[0] === action;
    });

    const availableGroupByEmptyPrivileges = resourceGroups.findIndex(group => {
      return (
        group.toAssign.length === 0 &&
        (group.objects.length === 0 || group.objects?.[0] === ALL_DATABASES)
      );
    });
    if (availableGroupByEmptyPrivileges !== -1) {
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource: "database",
        objects: [object],
        index: availableGroupByEmptyPrivileges,
      });
      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "database",
        privileges: [action],
        index: availableGroupByEmptyPrivileges,
      });
    } else if (availableGroupByObjects !== -1) {
      const group = resourceGroups[availableGroupByObjects];
      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "database",
        privileges: [...group.toAssign, action],
        index: availableGroupByObjects,
      });
    } else if (availableGroupByActions !== -1) {
      const group = resourceGroups[availableGroupByActions];
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource: "database",
        objects: [...group.objects, object],
        index: availableGroupByActions,
      });
    } else {
      privilegesDispatch({
        type: ROLES_WIZARD_ADD_GROUP_EMPTY,
        resource: "database",
        objects: [object],
      });

      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "database",
        privileges: [action],
        index: resourceGroups.length,
      });
    }
  };

  const ignorePermissionError = () => {
    privilegesDispatch({
      type: ROLES_WIZARD_IGNORE_ERROR,
      resource,
      error,
    });
  };

  const isBulk = bulkItemTitleMap[name as keyof typeof bulkItemTitleMap];
  const resourceName = isBulk
    ? t(bulkItemTitleMap[name as keyof typeof bulkItemTitleMap])
    : name;

  return (
    <MissingPermissionError
      addRelatedPermissions={addRelatedPermissions}
      ignorePermissionError={ignorePermissionError}
      primaryActionText={t("roles_wizard.missing_permission.add")}
      message={
        <Trans
          i18nKey="roles_wizard.missing_permission.message_database"
          components={{ bold: <strong /> }}
        />
      }
      messagePermission={
        <Trans
          i18nKey="roles_wizard.missing_permission.message_permission"
          values={{
            permission: actions.map(formatPrivilege).join(", "),
            resource: resourceName,

            resourceType: isBulk ? "" : resource,
          }}
          components={{ bold: <strong />, br: <br /> }}
        />
      }
    />
  );
};
